import { apiDelete, apiGet, apiPost } from "./apiClient";
import { normalizeOrderItemStatus } from "./orderItemStatuses";
import { normalizePaymentStatus } from "./paymentStatuses";

async function getCollection(path, errorMessage) {
  const res = await apiGet(path);
  if (!res.ok) return { ok: false, error: res.error ?? errorMessage, items: [] };
  return { ok: true, items: Array.isArray(res.data) ? res.data : [] };
}

export function fetchCouponsList() {
  return getCollection("/api/Coupons", "Failed to load coupons");
}

export function deleteCoupon(id) {
  return apiDelete(`/api/Coupons/${id}`);
}

export function fetchCouponUsagesByCouponId(couponId) {
  return getCollection(`/api/CouponUsages/by-coupon/${couponId}`, "Failed to load coupon usages");
}

export function fetchCartsList() {
  return getCollection("/api/Carts", "Failed to load cart items");
}

export function fetchWishlistsList() {
  return getCollection("/api/Wishlists", "Failed to load wishlists");
}

export function fetchOrdersList() {
  return getCollection("/api/Orders", "Failed to load orders");
}

export function fetchOrdersByCustomerId(customerId) {
  return getCollection(`/api/Orders/by-customer/${customerId}`, "Failed to load customer orders");
}

export function deleteOrder(id) {
  return apiDelete(`/api/Orders/${id}`);
}

export function normalizeOrder(row) {
  if (!row || typeof row !== "object") return null;
  const id = row.id ?? row.Id;
  if (id == null) return null;
  return {
    id,
    date: row.date ?? row.Date,
    customerId: row.customerId ?? row.CustomerId,
    orderStatusId: row.orderStatusId ?? row.OrderStatusId ?? 0,
    paymentStatus: normalizePaymentStatus(row.paymentStatus ?? row.PaymentStatus),
    orderNo: row.orderNo ?? row.OrderNo ?? "",
    paymentMethod: row.paymentMethod ?? row.PaymentMethod ?? "",
    transactionId: row.transactionId ?? row.TransactionId ?? "",
    subTotal: row.subTotal ?? row.SubTotal ?? 0,
    discount: row.discount ?? row.Discount ?? 0,
    taxable: row.taxable ?? row.Taxable ?? 0,
    taxAmount: row.taxAmount ?? row.TaxAmount ?? 0,
    deliveryCharges: row.deliveryCharges ?? row.DeliveryCharges ?? 0,
    grandTotal: row.grandTotal ?? row.GrandTotal ?? 0,
    customerAddressId: row.customerAddressId ?? row.CustomerAddressId,
    remarks: row.remarks ?? row.Remarks ?? "",
    deliveryDate: row.deliveryDate ?? row.DeliveryDate ?? null
  };
}

export function buildOrderSavePayload(order) {
  const normalized = normalizeOrder(order) ?? order;
  return {
    id: normalized.id ?? 0,
    date: normalized.date ? new Date(normalized.date).toISOString() : new Date().toISOString(),
    customerId: Number(normalized.customerId),
    orderStatusId: Number(normalized.orderStatusId || 0),
    paymentStatus: normalizePaymentStatus(normalized.paymentStatus),
    orderNo: normalized.orderNo || null,
    paymentMethod: normalized.paymentMethod || null,
    transactionId: normalized.transactionId || null,
    subTotal: Number(normalized.subTotal ?? 0),
    discount: Number(normalized.discount ?? 0),
    taxable: Number(normalized.taxable ?? 0),
    taxAmount: Number(normalized.taxAmount ?? 0),
    deliveryCharges: Number(normalized.deliveryCharges ?? 0),
    grandTotal: Number(normalized.grandTotal ?? 0),
    customerAddressId: Number(normalized.customerAddressId),
    remarks: normalized.remarks || null,
    deliveryDate: normalized.deliveryDate ? new Date(normalized.deliveryDate).toISOString() : null
  };
}

export async function saveOrder(order) {
  return apiPost("/api/Orders", buildOrderSavePayload(order));
}

export function fetchOrderDetailsList() {
  return getCollection("/api/OrderDetails", "Failed to load order details");
}

export function fetchOrderDetailsByOrderId(orderId) {
  return getCollection(`/api/OrderDetails/by-order/${orderId}`, "Failed to load order lines");
}

export function deleteOrderDetail(id) {
  return apiDelete(`/api/OrderDetails/${id}`);
}

/** Normalizes API order line to camelCase shape. */
export function normalizeOrderDetail(row) {
  if (!row || typeof row !== "object") return null;
  const id = row.id ?? row.Id;
  if (id == null) return null;
  return {
    id,
    orderId: row.orderId ?? row.OrderId,
    productId: row.productId ?? row.ProductId,
    vendorId: row.vendorId ?? row.VendorId,
    vendorAddressId: row.vendorAddressId ?? row.VendorAddressId,
    productVarientId: row.productVarientId ?? row.ProductVarientId,
    unitPrice: row.unitPrice ?? row.UnitPrice ?? 0,
    discount: row.discount ?? row.Discount ?? 0,
    taxable: row.taxable ?? row.Taxable ?? 0,
    taxAmount: row.taxAmount ?? row.TaxAmount ?? 0,
    grandTotal: row.grandTotal ?? row.GrandTotal ?? 0,
    itemStatus: normalizeOrderItemStatus(row.itemStatus ?? row.ItemStatus)
  };
}

export function buildOrderDetailSavePayload(line) {
  return {
    id: line.id ?? 0,
    orderId: Number(line.orderId),
    productId: Number(line.productId),
    vendorId: Number(line.vendorId),
    vendorAddressId: Number(line.vendorAddressId),
    productVarientId: Number(line.productVarientId),
    unitPrice: Number(line.unitPrice ?? 0),
    discount: Number(line.discount ?? 0),
    taxable: Number(line.taxable ?? 0),
    taxAmount: Number(line.taxAmount ?? 0),
    grandTotal: Number(line.grandTotal ?? 0),
    itemStatus: normalizeOrderItemStatus(line.itemStatus)
  };
}

export async function saveOrderDetail(line) {
  return apiPost("/api/OrderDetails", buildOrderDetailSavePayload(line));
}
